import { z } from 'zod';
import { codeSchema, isoDateTimeSchema, requiredText } from '../common/coercion.js';
import { SALE_ORDER_MODES } from '../decorapos/sale-group.js';
import { janisLocationAddressSchema, janisLocationCreateRequestSchema } from '../janis/location.js';
/**
 * Quien pickea una linea (S1, `docs/pedidos-de-tienda.md` §1.1):
 *
 * - `wms`: una bodega que administra Altanet (BOD1, BOD12, BOD17 ⚠️, a confirmar
 *   con el consultor SAP, pregunta 3). La pickea el WMS y la confirma F9.
 * - `store`: la bodega de una tienda. La pickea la tienda (los slices de
 *   tienda, S3 en adelante); F9 no la espera.
 * - `none`: no hay fisico que pickear. Hoy SOLO el `DESPACHO` (N9 de JP,
 *   2026-10-29: los no inventariables de SAP, en la etapa 2), este en la bodega
 *   que este.
 *
 * Se clasifica primero por item (`DESPACHO` → `none`) y despues por la bodega
 * de SAP de la linea, con `warehouse_config.fulfillment_source`. Una bodega sin
 * fuente es «sin clasificar»: nunca se adivina. El retiro con stock del CD
 * (`transfer`, §8) espera a S10–S12.
 */
export const FULFILLMENT_SOURCES = ['wms', 'store', 'none'];
export const fulfillmentSourceSchema = z.enum(FULFILLMENT_SOURCES);
/** La fuente de una BODEGA: `none` es de un item (`DESPACHO`), nunca de una bodega. */
export const WAREHOUSE_FULFILLMENT_SOURCES = ['wms', 'store'];
export const warehouseFulfillmentSourceSchema = z.enum(WAREHOUSE_FULFILLMENT_SOURCES);
/**
 * Como sale una linea (§1.2): el modo del envio de la venta (`SALE_ORDER_MODES`).
 * `counter` (meson) y `pickup` (retiro posterior) nunca van en una bodega `wms`.
 */
export const deliveryModeSchema = z.enum(SALE_ORDER_MODES);
/**
 * Mapeo de bodegas SAP -> Janis (F3).
 *
 * DECISION DEL 2026-08-27: **ninguna bodega se publica sola.** Cada una se
 * habilita explicitamente y queda registrado quien lo hizo. La alternativa
 * (publicar todas, o inferir por si el WMS las administra) obligaba a adivinar
 * cuales son de venta y cuales son de transito, merma o reparacion — y el error
 * ahi no se ve: aparece como stock fantasma en un marketplace.
 *
 * Esta tabla es tambien la brecha #9 del plan ("mapeo bodega->canal") convertida
 * en configuracion auditable en vez de una constante escondida en el codigo.
 */
export const warehouseMappingSchema = z
    .object({
    /** Codigo de la bodega en SAP (`OITW.WhsCode`). */
    sapWarehouseCode: requiredText(32),
    /**
     * Identificador con el que Janis conoce esta bodega, o `null` mientras la
     * bodega esta DESCUBIERTA en SAP y todavia sin location creada en Janis.
     *
     * VERIFICADO 2026-09-01: el middleware publica stock con la variante
     * `warehouseReferenceId`, o sea con la **referencia externa** de la bodega en
     * Janis, no con su objectId interno. Asi que aca va esa referencia — lo mas
     * simple y lo recomendado es que la bodega se cree en Janis con el mismo
     * codigo que tiene en SAP.
     *
     * NULLABLE A PROPOSITO, y esto es el arreglo de un agujero real. Cuando el
     * campo era obligatorio, el descubrimiento no tenia como decir "todavia no
     * hay mapeo" y registraba la bodega con su propio `WarehouseCode` de SAP.
     * Ese valor NO era un mapeo: era un placeholder indistinguible de uno real,
     * y `JanisStockPublisher` —que decide por "¿hay entrada para esta bodega?"—
     * lo encontraba y publicaba stock con una referencia que Janis puede no
     * conocer. Que sea `null` es lo que convierte "sin mapear" en un estado que
     * el sistema de tipos y la base obligan a mirar, en vez de uno que se
     * disfraza de mapeo.
     *
     * Que la referencia coincida con el codigo de SAP sigue siendo lo normal y
     * lo recomendado: la diferencia es que ahora ese valor solo llega aca cuando
     * alguien lo puso —`createInJanis` tras crear la location, o un operador a
     * mano— y nunca porque el descubrimiento lo haya rellenado solo.
     */
    janisWarehouseId: requiredText(64).nullable(),
    /**
     * Nombre legible, solo para la UI.
     *
     * Nullable y no opcional: esta fila viene de una tabla, y PostgreSQL devuelve
     * `null`, no `undefined`. Modelarlo como opcional obligaria a traducir en
     * cada borde.
     */
    label: z.string().max(120).nullable(),
    /**
     * Si esta en `false` la bodega NO se lee ni se publica. Es el interruptor:
     * apagarla es la forma de sacar una bodega de los canales sin borrar su
     * historial ni su mapeo.
     */
    enabled: z.boolean(),
    /**
     * El `referenceId` del Pickup Point de WMS de la tienda en Janis, o `null`
     * si la tienda no tiene (`docs/pedidos-de-tienda.md` §0 y §3.1). F1 lo
     * manda en `pickupInfo.pickupPointRefId` del meson y el retiro SOLO cuando
     * esta: mandarlo sin el Pickup Point creado deja el pedido en
     * `needsIntervention` (`dom.errors.pickupPointNotFound`). Opcional: las filas
     * de antes de la columna (migracion de S2) no lo traen. Se fija con su
     * propia ruta (`warehousePickupPointUpdateSchema`), no con el alta del mapeo.
     */
    pickupPointRefId: requiredText(64).nullable().optional(),
    /**
     * Quien pickea las lineas de esta bodega (S1): `wms` o `store`, o `null`
     * mientras nadie la clasifico. Con `null` una linea de la bodega queda «sin
     * clasificar» y F9 lo dice como incidente: no se adivina.
     *
     * Independiente de `enabled`: una tienda clasifica sus lineas aunque no
     * publique stock, y una bodega del WMS sigue siendo del WMS apagada.
     */
    fulfillmentSource: warehouseFulfillmentSourceSchema.nullable(),
    /** Correo de quien la habilito por ultima vez. Queda en el audit log. */
    updatedBy: z.string().max(160).nullable(),
    updatedAt: isoDateTimeSchema.nullable(),
})
    .strict();
/**
 * Bodegas de SAP que guardan mercaderia YA VENDIDA y por eso nunca se publican a
 * Janis, esten como esten en `warehouse_config` (decision del 2026-10-23).
 *
 * Desde ese dia F3 publica el fisico de SAP y Janis controla todas las reservas
 * (`./availability.js`). Una unidad vendida que espera en estas bodegas ya la
 * desconto Janis al venderla: si se publicara, Janis la volveria a vender.
 *
 * - **BOD24**, la bodega de transito de los traslados (reposiciones y retiros
 *   con stock del CD).
 *
 * Es una lista versionada y no una variable de entorno a proposito: el rol de
 * BOD24 es un hecho del diseno de SAP, igual en todos los ambientes, y una lista
 * que cada ambiente puede achicar es una forma de publicarla sin que nadie lo
 * decida. Agregar una bodega es un cambio de codigo revisado, y exige ademas una
 * migracion: el CHECK `warehouse_config_never_published_disabled` repite esta
 * regla en la base.
 */
export const NEVER_PUBLISHED_WAREHOUSE_CODES = ['BOD24'];
/**
 * Bodegas «por retirar» de cada tienda (`BODnR`, `docs/pedidos-de-tienda.md`
 * §8.3): la orden de venta de un retiro deja ahi lo que el cliente ya compro.
 *
 * ⚠️ Todavia no existen en SAP y su codigo lo da el consultor (pregunta 25). El
 * patron sigue la convencion de los documentos y la de los prestamos de cada
 * tienda (`BOD4P`); ninguna de las bodegas reales de hoy calza (lo fija un test
 * contra el fixture real). Si el consultor elige otro formato, hay que
 * agregarlo aca:
 *
 * - **Falso negativo** (una «por retirar» que no calza): hoy nada la ataja salvo
 *   que habilitar es un acto explicito de un operador. El CHECK de
 *   `warehouse_config.pickup_hold_for` (S10, por construir) lo cerrara.
 * - **Falso positivo** (una bodega de venta que calza): no se puede habilitar; el
 *   400 y el CHECK de la base lo dicen.
 */
const PICKUP_HOLD_WAREHOUSE_PATTERN = /^BOD\d+R$/;
/**
 * `true` si la bodega nunca publica stock a Janis.
 *
 * Es la unica definicion: la usan el alta de bodegas (el contrato rechaza
 * habilitarla), F3 (la saca de la corrida aunque este habilitada) y la pantalla
 * Bodegas del monitor.
 */
export function isNeverPublishedWarehouse(sapWarehouseCode) {
    const code = sapWarehouseCode.trim().toUpperCase();
    return (NEVER_PUBLISHED_WAREHOUSE_CODES.includes(code) ||
        PICKUP_HOLD_WAREHOUSE_PATTERN.test(code));
}
/**
 * Alta o edicion de un mapeo desde la UI. Solo el rol `operator`.
 *
 * LA REGLA: **habilitada exige referencia.** `enabled: true` con
 * `janisWarehouseId: null` es justo el estado que publica stock a una bodega que
 * Janis no conoce, asi que se rechaza en el borde, antes de tocar la base.
 *
 * Va en el contrato y no solo en el controlador porque es una regla del dato,
 * no de una ruta: la misma barrera existe como CHECK en `warehouse_config`, y
 * cualquier otro consumidor del paquete la hereda sin tener que acordarse.
 *
 * Al reves si se puede: apagar una bodega y quitarle la referencia a la vez es
 * como se revierte una que quedo mal configurada.
 *
 * Y **una bodega que nunca se publica no se habilita** (`isNeverPublishedWarehouse`):
 * guarda mercaderia ya vendida. Puede tener referencia en Janis; lo que no puede
 * es publicar.
 */
export const warehouseMappingUpsertSchema = warehouseMappingSchema
    .omit({ updatedBy: true, updatedAt: true, pickupPointRefId: true })
    // La fuente es opcional en el alta: sin ella no se toca (la pantalla Bodegas
    // de antes no la manda); `null` la borra.
    .extend({ fulfillmentSource: warehouseFulfillmentSourceSchema.nullable().optional() })
    .strict()
    .superRefine((mapping, ctx) => {
    if (mapping.enabled && isNeverPublishedWarehouse(mapping.sapWarehouseCode)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['enabled'],
            message: `${mapping.sapWarehouseCode} nunca publica stock a Janis: guarda mercaderia ya vendida ` +
                '(transito o «por retirar»), y publicarla haria que Janis la vendiera otra vez.',
        });
    }
    if (mapping.enabled && mapping.janisWarehouseId === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['janisWarehouseId'],
            message: 'No se puede habilitar una bodega sin su referencia en Janis: crea primero su location ' +
                '(POST /api/stock/warehouses/:code/janis) o indica el warehouseReferenceId a mano.',
        });
    }
});
/**
 * `POST /api/stock/warehouses/:code/pickup-point`: fija (o quita, con `null`)
 * el Pickup Point de WMS de la tienda. Va aparte del alta del mapeo porque es
 * otra decision: crear el Pickup Point en Janis es configuracion previa (§0), y
 * un alta del mapeo que no lo mande no lo puede borrar.
 */
export const warehousePickupPointUpdateSchema = z
    .object({ pickupPointRefId: requiredText(64).nullable() })
    .strict();
/**
 * Lo que el operador completa al crear la bodega en Janis: los datos que SAP no
 * puede dar.
 *
 * Se compone de los schemas VERIFICADOS de Janis en vez de declarar campos
 * nuevos, asi no hay dos verdades sobre como se llama cada campo. Quedan fuera
 * a proposito dos claves, y las dos por la misma razon —no son negociables
 * desde afuera—:
 *
 * - `referenceId`: lo fija el `WarehouseCode` de SAP. Es el valor que despues
 *   viaja como `warehouseReferenceId` en `POST wms/api/stock-batch`; uno
 *   distinto no rompe la creacion, rompe el `stock-batch` mucho despues.
 * - `warehouses`: el almacen lo arma el servicio, con ese mismo `referenceId`.
 *
 * `.strict()` y no `.passthrough()`: mandarle a Janis una clave que no espera es
 * el error que no avisa —la ignora en silencio y la location queda a medias—,
 * asi que se corta en el borde. Todo opcional: crear una bodega sin ningun dato
 * extra es valido y toma los defaults de Janis.
 */
export const warehouseLocationOverridesSchema = janisLocationCreateRequestSchema
    .omit({ referenceId: true, warehouses: true })
    .partial()
    .extend({ address: janisLocationAddressSchema.partial().optional() })
    .strict();
/**
 * Rutas de configuracion de bodegas (F3), relativas al prefijo global (`/api`).
 *
 * Viven aca por el principio 1: el contrato de un endpoint se publica en
 * OpenAPI, y estar en OpenAPI es lo que hace descubrible una ruta. Estas cuatro
 * no lo estaban, siendo las que deciden que bodega publica stock a los canales
 * — la escritura de mas consecuencia que hoy expone el middleware.
 *
 * Las consume la pantalla Bodegas de la UI de monitoreo (desde el 2026-09-30),
 * que construye sus URLs desde esta tabla: si una ruta cambia aca, cambia en
 * las dos puntas. Las escrituras siguen exigiendo el rol `operator` en la API.
 */
export const WAREHOUSE_API_PATHS = {
    list: '/stock/warehouses',
    upsert: '/stock/warehouses',
    discover: '/stock/warehouses/sync',
    createInJanis: '/stock/warehouses/{code}/janis',
};
/**
 * Resumen de una corrida de descubrimiento desde SAP.
 *
 * El numero que importa es `created`: TODAS nacen con `enabled = false`. El
 * descubrimiento registra y no habilita nada, y eso es el diseno — publicar
 * stock de una bodega de transito, merma o reparacion no se ve como un error,
 * se ve como stock fantasma en un marketplace.
 */
export const warehouseDiscoveryOutcomeSchema = z
    .object({
    /** Hilo con el que se abre la corrida completa en el message log. */
    correlationId: requiredText(128),
    /** Bodegas leidas de SAP. Verificado 2026-09-02: son 40. */
    read: z.number().int().nonnegative(),
    /** Registradas por primera vez. TODAS con `enabled = false`. */
    created: z.number().int().nonnegative(),
    /** Ya estaban: se refresco el label, sin tocar interruptor ni mapeo. */
    updated: z.number().int().nonnegative(),
    /** Leidas con `Inactive = "tYES"` en SAP. Se registran igual, marcadas. */
    inactiveInSap: z.number().int().nonnegative(),
})
    .strict();
/**
 * Resultado de crear la bodega en Janis.
 *
 * Dos campos que conviene leer antes de festejar:
 *
 * - `enabled` sigue en `false`. **Crear no habilita**: habilitar es un tercer
 *   acto explicito contra `POST /api/stock/warehouses`.
 * - `warehouseConfirmed` dice si la RELECTURA encontro el almacen dentro de la
 *   location. En `false` la bodega NO se debe habilitar todavia: puede ser que
 *   la location haya quedado sin almacen, y eso no aparece como error aca sino
 *   mucho despues, como un `stock-batch` que rechaza el `warehouseReferenceId`.
 */
export const warehouseProvisionOutcomeSchema = z
    .object({
    correlationId: requiredText(128),
    sapWarehouseCode: codeSchema(32),
    /** objectId de la location en Janis. */
    janisLocationId: requiredText(64),
    /** Referencia con la que se publica el stock (`warehouseReferenceId`). */
    janisWarehouseId: requiredText(64),
    /** Crear NO habilita: esto sigue en `false` hasta que alguien la encienda. */
    enabled: z.boolean(),
    /** La relectura encontro el almacen dentro de la location. */
    warehouseConfirmed: z.boolean(),
    /** Fila del message log, para abrir el payload crudo en la UI. */
    messageId: requiredText(64),
})
    .strict();
//# sourceMappingURL=warehouse.js.map